import PokolbinRiskAssessment from '../components/adhoc/PokolbinRiskAssessment';
/**
 * Rapid X - Adhoc / Random Response Jobs
 *
 * Dual-mode page:
 *   - Admin/Supervisor/Control Room: full dispatch console (list, filters, create, reassign, cancel).
 *   - Officer: "My Queue" — accept/en-route/on-site/complete with GPS + closeout.
 *
 * Design notes:
 *   - No push/email alerts — officers refresh ("polling" model per product owner).
 *   - NO geofence / roster-lock enforcement — this is an ad-hoc dispatch flow.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Zap, Plus, RefreshCw, MapPin, Clock, User as UserIcon,
  CheckCircle2, PlayCircle, Navigation, XCircle, Loader2,
  AlertTriangle, Send, Camera, Shield,
} from 'lucide-react';

const PRIORITY_COLORS = {
  low:      'bg-blue-500/20 text-blue-400 border-blue-500/30',
  normal:   'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',
  high:     'bg-orange-500/20 text-orange-400 border-orange-500/30',
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const STATUS_COLORS = {
  dispatched: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  accepted:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  en_route:   'bg-violet-500/20 text-violet-300 border-violet-500/30',
  on_site:    'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  completed:  'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  cancelled:  'bg-zinc-600/20 text-zinc-500 border-zinc-600/30',
};

const JOB_TYPES = [
  { value: 'alarm_response', label: 'Alarm Response' },
  { value: 'welfare_check', label: 'Welfare Check' },
  { value: 'client_request', label: 'Client Request' },
  { value: 'lock_unlock', label: 'Lock / Unlock' },
  { value: 'patrol_adhoc', label: 'Ad-hoc Patrol' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = ['low', 'normal', 'high', 'critical'];

const DISPATCHER_ROLES = ['super_admin', 'operations_admin', 'supervisor', 'control_room'];

function getGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_meters: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
    );
  });
}

export default function AdhocJobsPage() {
  const { api, user } = useAuth();
  const isDispatcher = useMemo(() => DISPATCHER_ROLES.includes(user?.role), [user]);
  const isOfficer = user?.role === 'security_officer';
  const isSuperAdmin = user?.role === 'super_admin';
  const [deleteJob, setDeleteJob] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState(isOfficer ? 'active' : 'all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);

  // Create modal
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    site_id: '',
    job_type: 'alarm_response',
    priority: 'normal',
    instructions: '',
    assigned_to: '',
    client_ref: '',
    sla_minutes: '',
    eta_minutes: '',
    billable: true,
  });

  // Complete / cancel modals
  const [completeJob, setCompleteJob] = useState(null);
  const [cancelJob, setCancelJob] = useState(null);
  const [closeoutNotes, setCloseoutNotes] = useState('');
  const [billableMinutes, setBillableMinutes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Photo evidence modal
  const [photoJob, setPhotoJob] = useState(null);
  const [riskJob, setRiskJob] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isOfficer ? '/adhoc-jobs/my' : '/adhoc-jobs';
      const params = {};
      if (!isOfficer) {
        if (filterStatus === 'active') params.active_only = true;
        else if (filterStatus !== 'all') params.status = filterStatus;
        if (filterPriority !== 'all') params.priority = filterPriority;
      }
      const res = await api.get(endpoint, { params });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load adhoc jobs');
    } finally {
      setLoading(false);
    }
  }, [api, isOfficer, filterStatus, filterPriority]);

  const loadRefData = useCallback(async () => {
    if (!isDispatcher) return;
    try {
      const [sitesRes, usersRes] = await Promise.all([
        api.get('/sites', { params: { limit: 500 } }),
        api.get('/users', { params: { limit: 500 } }),
      ]);
      const sitesList = sitesRes.data?.items || sitesRes.data || [];
      const usersList = usersRes.data?.items || usersRes.data?.users || usersRes.data || [];
      setSites(Array.isArray(sitesList) ? sitesList : []);
      setOfficers(
        (Array.isArray(usersList) ? usersList : []).filter(u =>
          ['security_officer', 'supervisor'].includes(u.role),
        ),
      );
    } catch (e) {
      // non-fatal
    }
  }, [api, isDispatcher]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadRefData(); }, [loadRefData]);

  // Officer auto-refresh every 20s (polling model)
  useEffect(() => {
    if (!isOfficer) return;
    const id = setInterval(loadJobs, 20000);
    return () => clearInterval(id);
  }, [isOfficer, loadJobs]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.site_id || !form.instructions.trim()) {
      toast.error('Site and Instructions are required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        site_id: form.site_id,
        job_type: form.job_type,
        priority: form.priority,
        instructions: form.instructions.trim(),
        billable: form.billable,
      };
      if (form.assigned_to) payload.assigned_to = form.assigned_to;
      if (form.client_ref) payload.client_ref = form.client_ref.trim();
      if (form.sla_minutes) payload.sla_minutes = Number(form.sla_minutes);
      if (form.eta_minutes) payload.eta_minutes = Number(form.eta_minutes);

      await api.post('/adhoc-jobs', payload);
      toast.success('Adhoc job dispatched');
      setOpenCreate(false);
      setForm({
        site_id: '', job_type: 'alarm_response', priority: 'normal',
        instructions: '', assigned_to: '', client_ref: '',
        sla_minutes: '', eta_minutes: '', billable: true,
      });
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  }

  async function transition(jobId, target, extraBody = {}) {
    setBusy(true);
    try {
      const gps = await getGps();
      const body = { gps, ...extraBody };
      await api.post(`/adhoc-jobs/${jobId}/${target}`, body);
      toast.success(`Marked ${target.replace('-', ' ')}`);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${target}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleComplete() {
    if (!completeJob) return;
    if (!closeoutNotes.trim()) {
      toast.error('Closeout notes are required');
      return;
    }
    if ((completeJob.photos_count || 0) < 1) {
      toast.error('At least one photo is required before completing this job');
      return;
    }
    setBusy(true);
    try {
      const gps = await getGps();
      const body = { closeout_notes: closeoutNotes.trim(), gps };
      if (billableMinutes) body.billable_minutes = Number(billableMinutes);
      await api.post(`/adhoc-jobs/${completeJob.id}/complete`, body);
      toast.success('Job completed');
      setCompleteJob(null);
      setCloseoutNotes('');
      setBillableMinutes('');
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete');
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!cancelJob) return;
    if (!cancelReason.trim()) {
      toast.error('Cancellation reason required');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/adhoc-jobs/${cancelJob.id}/cancel`, { reason: cancelReason.trim() });
      toast.success('Job cancelled');
      setCancelJob(null);
      setCancelReason('');
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteJob() {
    if (!isSuperAdmin || !deleteJob || deleting) return;
    if (deleteConfirmation !== deleteJob.job_number ||
        deleteReason.trim().length < 3) return;

    setDeleting(true);
    try {
      await api.delete(`/adhoc-jobs/${deleteJob.id}`, {
        data: {
          reason: deleteReason.trim(),
          expected_job_number: deleteConfirmation,
        },
      });
      toast.success('Job deleted from operational lists; history retained');
      setDeleteJob(null);
      setDeleteReason('');
      setDeleteConfirmation('');
      await loadJobs();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete job');
    } finally {
      setDeleting(false);
    }
  }

  async function handleUploadPhoto() {
    if (!photoJob || !photoFile) {
      toast.error('Select a photo first');
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);

      const gps = await getGps();
      if (gps) {
        formData.append('gps_lat', gps.latitude);
        formData.append('gps_lng', gps.longitude);
        formData.append('captured_at', new Date().toISOString());
      }

      await api.post(`/adhoc-jobs/${photoJob.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Photo evidence uploaded');
      setPhotoJob(null);
      setPhotoFile(null);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100" data-testid="adhoc-jobs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Zap className="w-8 h-8 text-amber-400" />
              {isOfficer ? 'My Adhoc Jobs' : 'Adhoc Response Jobs'}
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              {isOfficer
                ? 'Random response tasks assigned to you (auto-refreshes every 20s).'
                : 'Dispatch ad-hoc jobs: alarms, welfare checks, client requests.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadJobs}
              disabled={loading}
              className="border-zinc-700"
              data-testid="adhoc-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {isDispatcher && (
              <Button
                onClick={() => setOpenCreate(true)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="adhoc-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Adhoc Job
              </Button>
            )}
          </div>
        </div>

        {/* Filters (admin only) */}
        {!isOfficer && (
          <Card className="bg-zinc-900/50 border-zinc-800 mb-4">
            <CardContent className="p-4 flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-400">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40 bg-zinc-950 border-zinc-700" data-testid="adhoc-filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="dispatched">Dispatched</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="en_route">En Route</SelectItem>
                    <SelectItem value="on_site">On Site</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-400">Priority</Label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-32 bg-zinc-950 border-zinc-700" data-testid="adhoc-filter-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Jobs list */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-16 text-center text-zinc-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
              <p className="text-lg">No adhoc jobs</p>
              <p className="text-sm mt-1">
                {isDispatcher ? 'Click "New Adhoc Job" to dispatch one.' : 'You have no active assignments.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="adhoc-jobs-list">
            {items.map(job => (
              <JobCard
                key={job.id}
                job={job}
                isDispatcher={isDispatcher}
                isOfficer={isOfficer}
                busy={busy}
                onTransition={transition}
                onComplete={() => setCompleteJob(job)}
                onCancel={() => setCancelJob(job)}
                onPhoto={() => setPhotoJob(job)}
                onRiskAssessment={() => setRiskJob(job)}
                isSuperAdmin={isSuperAdmin}
                onDelete={() => {
                  setDeleteReason('');
                  setDeleteConfirmation('');
                  setDeleteJob(job);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pokolbin site-access risk assessment pilot */}
      <Dialog
        open={!!riskJob}
        onOpenChange={(open) => {
          if (!open) setRiskJob(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Site Access Risk Assessment</DialogTitle>
          </DialogHeader>
          {riskJob && (
            <PokolbinRiskAssessment
              onClose={() => setRiskJob(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Super-admin recoverable deletion */}
      <Dialog open={!!deleteJob} onOpenChange={(open) => {
        if (!open && !deleting) setDeleteJob(null);
      }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle>Delete Job {deleteJob?.job_number}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Remove this completed/cancelled job from operational lists.
              Its record, photos and history remain stored for recovery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="adhoc-delete-reason">Reason *</Label>
            <Textarea id="adhoc-delete-reason" value={deleteReason}
              onChange={(event) => setDeleteReason(event.target.value)}
              maxLength={1000} disabled={deleting}
              placeholder="For example: test response job"
              className="bg-zinc-900 border-zinc-700" />
            <Label htmlFor="adhoc-delete-confirmation">
              Type {deleteJob?.job_number} to confirm *
            </Label>
            <Input id="adhoc-delete-confirmation" value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              disabled={deleting}
              className="bg-zinc-900 border-zinc-700" />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={deleting}
              onClick={() => setDeleteJob(null)}>Back</Button>
            <Button onClick={handleDeleteJob}
              disabled={deleting || deleteReason.trim().length < 3 ||
                !deleteJob || deleteConfirmation !== deleteJob.job_number}
              className="bg-red-700 hover:bg-red-800"
              data-testid="adhoc-delete-submit">
              {deleting ? 'Deleting...' : 'Delete job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create modal */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" /> Dispatch Adhoc Job
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              No geofence, no roster lock. Officer will see it on their dashboard within 20s.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Site *</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="adhoc-form-site">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Assign To</Label>
                <Select value={form.assigned_to || 'pool'} onValueChange={(v) => setForm({ ...form, assigned_to: v === 'pool' ? '' : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="adhoc-form-officer">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pool">Unassigned (pool)</SelectItem>
                    {officers.map(o => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.first_name} {o.last_name} — {o.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Job Type</Label>
                <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="adhoc-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="adhoc-form-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">SLA (minutes)</Label>
                <Input
                  type="number" min="1" max="1440"
                  value={form.sla_minutes}
                  onChange={(e) => setForm({ ...form, sla_minutes: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="adhoc-form-sla"
                />
              </div>
              <div>
                <Label className="text-zinc-300">ETA (minutes)</Label>
                <Input
                  type="number" min="1" max="1440"
                  value={form.eta_minutes}
                  onChange={(e) => setForm({ ...form, eta_minutes: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="adhoc-form-eta"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-300">Client Reference</Label>
                <Input
                  value={form.client_ref}
                  onChange={(e) => setForm({ ...form, client_ref: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  placeholder="Optional — e.g., ALRM-0029"
                  data-testid="adhoc-form-clientref"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-300">Instructions *</Label>
                <Textarea
                  value={form.instructions}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 min-h-[100px]"
                  placeholder="Respond to alarm at rear roller door. Check doors/windows, sweep perimeter, report anomalies."
                  data-testid="adhoc-form-instructions"
                />
              </div>
              <label className="flex items-center gap-2 md:col-span-2 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  checked={form.billable}
                  onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                  data-testid="adhoc-form-billable"
                />
                Billable to client
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-amber-600 hover:bg-amber-700" data-testid="adhoc-form-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Dispatch
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo evidence modal */}
      <Dialog open={!!photoJob} onOpenChange={(v) => { if (!v) { setPhotoJob(null); setPhotoFile(null); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-400" /> Add Photo Evidence {photoJob?.job_number}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Upload at least one photo before completing this adhoc job.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-zinc-300">Photo *</Label>
            <Input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              className="bg-zinc-900 border-zinc-700"
              data-testid="adhoc-photo-input"
            />
            {photoJob?.photos_count > 0 && (
              <p className="text-sm text-emerald-400">
                Existing photo evidence: {photoJob.photos_count}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setPhotoJob(null); setPhotoFile(null); }} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleUploadPhoto}
              disabled={uploadingPhoto || !photoFile}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="adhoc-photo-upload-submit"
            >
              {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
              Upload Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete modal */}
<Dialog open={!!completeJob} onOpenChange={(v) => !v && setCompleteJob(null)}>
  <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Complete Job {completeJob?.job_number}
      </DialogTitle>
      <DialogDescription className="text-zinc-400">
        Upload photo evidence and write a short closeout describing what you found / did.
      </DialogDescription>
    </DialogHeader>

    <div className="space-y-3">
      <div>
        <Label className="text-zinc-300">Photo Evidence *</Label>
        <Input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
          className="bg-zinc-900 border-zinc-700"
          data-testid="adhoc-complete-photo-input"
        />

        {completeJob?.photos_count > 0 && (
          <p className="text-sm text-emerald-400 mt-1">
            Existing photos: {completeJob.photos_count}
          </p>
        )}

        <Button
          type="button"
          onClick={handleUploadPhoto}
          disabled={uploadingPhoto || !photoFile}
          className="bg-amber-600 hover:bg-amber-700 mt-2"
          data-testid="adhoc-complete-photo-upload"
        >
          {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
          Upload Photo
        </Button>
      </div>

      <div>
        <Label className="text-zinc-300">Closeout Notes *</Label>
        <Textarea
          value={closeoutNotes}
          onChange={(e) => setCloseoutNotes(e.target.value)}
          className="bg-zinc-900 border-zinc-700 min-h-[120px]"
          data-testid="adhoc-closeout-notes"
        />
      </div>

      <div>
        <Label className="text-zinc-300">Billable Minutes</Label>
        <Input
          type="number" min="0"
          value={billableMinutes}
          onChange={(e) => setBillableMinutes(e.target.value)}
          className="bg-zinc-900 border-zinc-700"
          data-testid="adhoc-billable-minutes"
        />
      </div>
    </div>

    <DialogFooter>
      <Button variant="outline" className="border-zinc-700" onClick={() => setCompleteJob(null)}>Cancel</Button>
      <Button
        onClick={handleComplete}
        disabled={busy || (completeJob?.photos_count || 0) < 1}
        className="bg-emerald-600 hover:bg-emerald-700"
        data-testid="adhoc-complete-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
        Mark Complete
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>

      {/* Cancel modal */}
      <Dialog open={!!cancelJob} onOpenChange={(v) => !v && setCancelJob(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" /> Cancel Job {cancelJob?.job_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-zinc-300">Reason *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="bg-zinc-900 border-zinc-700 min-h-[100px]"
              data-testid="adhoc-cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setCancelJob(null)}>Back</Button>
            <Button
              onClick={handleCancel}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="adhoc-cancel-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Cancel Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function JobCard({ job, isDispatcher, isOfficer, isSuperAdmin, busy, onTransition, onComplete, onCancel, onPhoto, onDelete, onRiskAssessment }) {
  const canAdvance = isOfficer || isDispatcher;
  const status = job.status;

  const actions = [];
  if (
    job.id === '6ad7f9704c3eeef6cb64c77c' &&
    job.job_number === 'AJ-2026-0008' &&
    job.client_ref === 'WHS-POK-001' &&
    (isOfficer || isDispatcher)
  ) {
    actions.push(
      <Button
        key="risk-assessment"
        size="sm"
        variant="outline"
        disabled={busy}
        className="border-amber-600 text-amber-300"
        onClick={onRiskAssessment}
        data-testid={`adhoc-risk-assessment-${job.id}`}
      >
        <AlertTriangle className="w-4 h-4 mr-1" />
        Risk Assessment
      </Button>
    );
  }

  if (isSuperAdmin && ['completed', 'cancelled'].includes(status)) {
    actions.push(
      <Button key="delete" size="sm" variant="outline" disabled={busy}
        className="border-red-800 text-red-400 hover:bg-red-900/20"
        onClick={onDelete} data-testid={`adhoc-delete-${job.id}`}>
        <XCircle className="w-4 h-4 mr-1" /> Delete
      </Button>
    );
  }
  if (canAdvance && status === 'dispatched') {
    actions.push(
      <Button key="accept" size="sm" disabled={busy} className="bg-blue-600 hover:bg-blue-700"
              onClick={() => onTransition(job.id, 'accept')} data-testid={`adhoc-accept-${job.id}`}>
        <PlayCircle className="w-4 h-4 mr-1" /> Accept
      </Button>,
    );
  }
  if (canAdvance && status === 'accepted') {
    actions.push(
      <Button key="enroute" size="sm" disabled={busy} className="bg-violet-600 hover:bg-violet-700"
              onClick={() => onTransition(job.id, 'en-route')} data-testid={`adhoc-enroute-${job.id}`}>
        <Navigation className="w-4 h-4 mr-1" /> En Route
      </Button>,
    );
  }
  if (canAdvance && (status === 'en_route' || status === 'accepted')) {
    actions.push(
      <Button key="onsite" size="sm" disabled={busy} className="bg-cyan-600 hover:bg-cyan-700"
              onClick={() => onTransition(job.id, 'on-site')} data-testid={`adhoc-onsite-${job.id}`}>
        <MapPin className="w-4 h-4 mr-1" /> On Site
      </Button>,
    );
  }
  if (canAdvance && !['completed', 'cancelled'].includes(status)) {
    actions.push(
      <Button key="photo" size="sm" variant="outline" disabled={busy}
              className="border-amber-700 text-amber-300 hover:bg-amber-900/20"
              onClick={onPhoto} data-testid={`adhoc-photo-btn-${job.id}`}>
        <Camera className="w-4 h-4 mr-1" /> Add Photo
      </Button>,
    );
  }
  if (canAdvance && status === 'on_site') {
    actions.push(
      <Button key="complete" size="sm" disabled={busy || (job.photos_count || 0) < 1} className="bg-emerald-600 hover:bg-emerald-700"
              onClick={onComplete} data-testid={`adhoc-complete-${job.id}`}>
        <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
      </Button>,
    );
  }
  if (isDispatcher && !['completed', 'cancelled'].includes(status)) {
    actions.push(
      <Button key="cancel" size="sm" variant="outline" disabled={busy}
              className="border-red-800 text-red-400 hover:bg-red-900/20"
              onClick={onCancel} data-testid={`adhoc-cancel-btn-${job.id}`}>
        <XCircle className="w-4 h-4 mr-1" /> Cancel
      </Button>,
    );
  }

  return (
    <Card className="bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 transition" data-testid={`adhoc-job-${job.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="font-mono text-sm text-amber-400">{job.job_number}</span>
              <Badge className={`${STATUS_COLORS[status] || STATUS_COLORS.dispatched}`}>
                {status.replace('_', ' ')}
              </Badge>
              <Badge className={PRIORITY_COLORS[job.priority] || PRIORITY_COLORS.normal}>
                {job.priority}
              </Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                {JOB_TYPES.find(t => t.value === job.job_type)?.label || job.job_type}
              </Badge>
              {job.billable === false && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-500">non-billable</Badge>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-zinc-300 mb-1">
              <MapPin className="w-4 h-4 text-zinc-500" />
              <span className="font-semibold">{job.site_name}</span>
              {job.client_name && (
                <span className="text-zinc-500">· {job.client_name}</span>
              )}
              {job.client_ref && (
                <span className="text-xs text-zinc-500 font-mono">[{job.client_ref}]</span>
              )}
            </div>

            <p className="text-sm text-zinc-300 mb-2 whitespace-pre-wrap">{job.instructions}</p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Dispatched {formatDateTime(job.created_at)}
              </span>
              {job.assigned_to_name && (
                <span className="flex items-center gap-1">
                  <UserIcon className="w-3 h-3" /> {job.assigned_to_name}
                </span>
              )}
              {job.sla_minutes && (
                <span className="flex items-center gap-1 text-amber-500">
                  <AlertTriangle className="w-3 h-3" /> SLA {job.sla_minutes}m
                </span>
              )}
              {job.eta_minutes && <span>ETA {job.eta_minutes}m</span>}
              {job.photos_count > 0 && (
                <span className="flex items-center gap-1">
                  <Camera className="w-3 h-3" /> {job.photos_count}
                </span>
              )}
            </div>

            {status === 'completed' && job.closeout_notes && (
              <div className="mt-2 p-2 bg-emerald-950/30 border border-emerald-900/50 rounded text-sm text-emerald-200">
                <span className="font-semibold text-emerald-400">Closeout:</span> {job.closeout_notes}
                {job.billable_minutes ? <span className="ml-2 text-emerald-500 text-xs">({job.billable_minutes}m billable)</span> : null}
              </div>
            )}
            {status === 'cancelled' && job.cancelled_reason && (
              <div className="mt-2 p-2 bg-red-950/30 border border-red-900/50 rounded text-sm text-red-200">
                <span className="font-semibold text-red-400">Cancelled:</span> {job.cancelled_reason}
              </div>
            )}
          </div>

          {actions.length > 0 && (
            <div className="flex flex-col gap-2 lg:w-44">
              {actions}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

